import 'client-only';

import { logger } from '@curology/logger/client';
import { useEffect, useRef, useState } from 'react';

import { useStripeClient } from '@/components/payments/hooks/useStripeClient';
import { createPaymentRequestOptions } from '@/components/payments/utils';
import { type PaymentInvoice } from '@/types/payments/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

class BrowserPaymentRequest {
  paymentRequest: stripe.paymentRequest.StripePaymentRequest;

  canMakeApplePayPayments: boolean;

  onSuccess: (
    token: stripe.paymentRequest.StripeTokenPaymentResponse,
  ) => Promise<void>;

  constructor(
    paymentRequest: stripe.paymentRequest.StripePaymentRequest,
    canMakeApplePayPayments: boolean,
  ) {
    this.paymentRequest = paymentRequest;

    this.canMakeApplePayPayments = canMakeApplePayPayments;

    this.onSuccess = async () => {};

    this.paymentRequest.on('cancel', () => {
      if (this.canMakeApplePayPayments) {
        trackEvent(AnalyticsEvent.APPLE_PAY_CANCELED);
      }
    });

    this.paymentRequest.on('token', async (event) => {
      await this.onSuccess(event);
      event.complete('success');
    });
  }

  get buttonId() {
    return this.canMakeApplePayPayments
      ? 'apple-pay-button'
      : 'browser-payment-button';
  }

  get buttonContainerId() {
    return this.canMakeApplePayPayments
      ? 'apple-pay-button-container'
      : 'browser-payment-button-container';
  }

  registerCallbacks = (
    onSuccess: (
      token: stripe.paymentRequest.StripeTokenPaymentResponse,
    ) => Promise<void>,
  ) => {
    this.onSuccess = onSuccess;
  };
}

export type BrowserPaymentRequestType = BrowserPaymentRequest | null;

/**
 * The Browser Payment client initializes a Browser Payment request in the Stripe API. The payment
 * request can be used to render an Apple (or Google) Pay Button and retrieve a one-time nonce.
 * When using Browser Payments for express checkouts, `requestPayerInfo` should be true.
 *
 * We use stripe to render and process Apple Pay. Wrap the page in the StripeClientProvider
 * to ensure the Stripe client context is loaded.
 */
export const useBrowserPayment = (
  paymentInvoice: PaymentInvoice,
  requestPayerInfo = false,
) => {
  const stripeClient = useStripeClient();
  const [paymentRequestLoaded, setPaymentRequestLoaded] = useState(false);
  const browserPaymentRequest = useRef<BrowserPaymentRequestType>(null);

  useEffect(() => {
    if (stripeClient) {
      const currency = paymentInvoice.currency || {
        country: 'US',
        currency: 'usd',
      };
      const options = createPaymentRequestOptions(requestPayerInfo);
      const paymentRequest = stripeClient.paymentRequest({
        displayItems: paymentInvoice.paymentLineItems,
        country: currency.country,
        currency: currency.currency,
        requestPayerEmail: options.requestPayerEmail,
        requestPayerName: options.requestPayerName,
        requestPayerPhone: options.requestPayerPhone,
        requestShipping: options.requestShipping,
        shippingOptions: paymentInvoice.shippingOptions,
        total: paymentInvoice.total,
      });

      initializeBrowserPayments(paymentRequest)
        .catch(() => logger.log('Failed to load browser payments processor.'))
        .finally(() => setPaymentRequestLoaded(true));
    }
  }, [stripeClient, paymentInvoice, requestPayerInfo]);

  const initializeBrowserPayments = async (
    paymentRequest: stripe.paymentRequest.StripePaymentRequest,
  ): Promise<void> => {
    const availableBrowserPayments = await paymentRequest.canMakePayment();

    if (availableBrowserPayments?.applePay) {
      trackEvent(AnalyticsEvent.APPLE_PAY_SHOWN);
      browserPaymentRequest.current = new BrowserPaymentRequest(
        paymentRequest,
        true,
      );
    }
  };

  return {
    request: browserPaymentRequest,
    canMakeApplePayPayments:
      !!browserPaymentRequest.current?.canMakeApplePayPayments,
    isLoading: !paymentRequestLoaded,
  };
};
